import isEmpty from "lodash/isEmpty";
import { useMutation } from "@stadium/services/apollo-client";
import { showError } from "@stadium/services/react-toastify";

import { useCreateSwagContext } from "@contexts/create-swag-context";
import useDraftCatalogSelectedProductsPresence from "@hooks/automated-catalog/use-draft-catalog-selected-products-presence";
import { POPULATE_DRAFT_AUTOMATED_CATALOG } from "@graphql/mutations/store/populate-draft-automated-catalog";
import { REMOVE_PRODUCT_FROM_DRAFT_AUTOMATED_CATALOG } from "@graphql/mutations/store/remove-product-from-draft-automated-catalog";
import {
  buildCollectionAfterColorRemoval,
  logoMappedProductMatchesRemoval,
  productMatchesId,
  serializeDraftCatalogProducts,
} from "@helpers/create-swag";

const DEFAULT_PER_PAGE = 30;

const usePopulateAutomatedCatalog = () => {
  const {
    draftSwagData,
    setDraftCatalogSelectedProductPresence,
    setDraftCatalogSelectedProductsPresence,
    setSwagCollection,
    setSwagCollectionMeta,
    swagCollection,
    setSwagLogos,
    draftCatalogSelectedProductsPresence,
  } = useCreateSwagContext();
  const catalogNumber = draftSwagData?.number;
  const { fetchSelectedProductsPresence } = useDraftCatalogSelectedProductsPresence();

  const updateSwagCollectionData = (paginatedProducts, products = null) => {
    const serializedProducts = serializeDraftCatalogProducts(paginatedProducts?.products || []);
    setSwagCollection(products || serializedProducts);
    setSwagCollectionMeta({
      total: paginatedProducts?.total || 0,
      countryWiseProductCount: paginatedProducts?.countryWiseProductCount || {},
      globalProductCount: paginatedProducts?.globalProductCount || 0,
      totalColorOptions: paginatedProducts?.totalColorOptions || 0,
      totalProductsCount: paginatedProducts?.totalProductsCount || 0,
    });
  };

  const [populateDraftAutomatedCatalog, { loading: isPopulatingDraftAutomatedCatalog }] =
    useMutation(POPULATE_DRAFT_AUTOMATED_CATALOG);

  const [
    removeProductFromDraftAutomatedCatalog,
    { loading: isRemovingProductFromDraftAutomatedCatalog },
  ] = useMutation(REMOVE_PRODUCT_FROM_DRAFT_AUTOMATED_CATALOG);

  const loading = isPopulatingDraftAutomatedCatalog || isRemovingProductFromDraftAutomatedCatalog;

  const addProductToAutomatedCatalog = async ({ productId, colorIds, filters }) => {
    const input = {
      catalogNumber,
      perPage: DEFAULT_PER_PAGE,
    };

    if (filters) {
      input.filters = filters;
    } else {
      input.selectedProduct = {
        ...(colorIds?.length ? { colorIds } : {}),
        productId,
      };
    }

    try {
      const { data } = await populateDraftAutomatedCatalog({ variables: { input } });
      const payload = data?.populateDraftAutomatedCatalog;
      const errors = payload?.errors || [];

      if (errors?.length) {
        errors.forEach((error) => showError(error));
        return { success: false, payload };
      }

      updateSwagCollectionData(payload?.paginatedProducts);
      setDraftCatalogSelectedProductPresence({
        productId,
        isSelected: true,
        allAll: !isEmpty(filters),
      });
      return { success: true, payload };
    } catch (e) {
      showError("Unable to add product to automated catalog.");
      return { success: false };
    }
  };

  const removeProductFromAutomatedCatalog = async ({
    productId,
    colorId = null,
    removeAllProducts = false,
    iso,
    globalZone = false,
  }) => {
    const input = removeAllProducts
      ? {
          catalogNumber,
          removeAllProducts: true,
          ...(iso ? { iso } : {}),
          ...(globalZone ? { globalZone } : {}),
        }
      : {
          catalogNumber,
          productId,
          perPage: DEFAULT_PER_PAGE,
          ...(colorId ? { colorOptionValueId: colorId } : {}),
        };

    try {
      const { data } = await removeProductFromDraftAutomatedCatalog({ variables: { input } });
      const payload = data?.removeProductFromDraftAutomatedCatalog;
      const errors = payload?.errors || [];

      if (errors?.length) {
        errors.forEach((error) => showError(error));
        return { success: false, payload };
      }

      if (removeAllProducts) {
        const isCountryScopedRemoval = Boolean(iso || globalZone);
        updateSwagCollectionData(payload?.paginatedProducts, []);

        setSwagLogos((prev) => prev.map((logo) => ({ ...logo, products: [] })));
        if (!isCountryScopedRemoval) {
          setDraftCatalogSelectedProductsPresence({});
          return { success: true, payload };
        }

        const scopedProductIds = Object.keys(draftCatalogSelectedProductsPresence || {})
          .map(Number)
          .filter(Boolean);

        const refreshedPresence = await fetchSelectedProductsPresence(scopedProductIds);
        setDraftCatalogSelectedProductsPresence(refreshedPresence || {});
        return { success: true, payload };
      }

      let nextProducts;
      if (colorId == null) {
        nextProducts = swagCollection.filter((product) => !productMatchesId(product, productId));
      } else {
        nextProducts = buildCollectionAfterColorRemoval(swagCollection, productId, colorId);
      }

      updateSwagCollectionData(payload?.paginatedProducts, nextProducts);

      const productFullyRemoved = !nextProducts.some((p) => productMatchesId(p, productId));
      if (productFullyRemoved) {
        setDraftCatalogSelectedProductPresence({ productId, isSelected: false });
      }

      setSwagLogos((prev) =>
        prev.map((logo) => ({
          ...logo,
          products: (logo.products || []).filter(
            (p) => !logoMappedProductMatchesRemoval(p, productId, colorId),
          ),
        })),
      );

      return { success: true, payload };
    } catch (e) {
      showError("Unable to remove product from automated catalog.");
      return { success: false };
    }
  };

  return {
    addProductToAutomatedCatalog,
    removeProductFromAutomatedCatalog,
    loading,
    isPopulatingDraftAutomatedCatalog,
  };
};

export default usePopulateAutomatedCatalog;
